import {
  CREATOR_SCRIPT_JOB_STATUS,
  ICreatorScriptGeneratorJobDetail,
} from "@entities/aiWorkspace";
import { useConceptualiseCreatorScript } from "@entities/aiWorkspace/lib/hooks/useConceptualiseCreatorScript";
import { ToastService } from "@shared/lib/services";
import { useEffect, useRef, useState } from "react";
import { ScriptDraft, ScriptSettings } from "./types";

function userInputs(draft: ScriptDraft) {
  return {
    brief: draft.brief.trim(),
    format: draft.settings.format.toLowerCase(),
    duration: draft.settings.duration,
    toneBalance: draft.settings.tone,
    language: draft.settings.language,
  };
}

const JOB_POLL_MS = 3000;

export function useScriptConceptStage(
  active: ScriptDraft,
  job: ICreatorScriptGeneratorJobDetail | undefined,
  onChange: (patch: Partial<Pick<ScriptDraft, "brief" | "settings">>) => void,
  refetchJob: () => void
) {
  const draftRef = useRef(active);
  const onChangeRef = useRef(onChange);
  draftRef.current = active;
  onChangeRef.current = onChange;
  const { conceptualise } = useConceptualiseCreatorScript();
  const revertRef = useRef<(() => void) | null>(null);
  const submittedMarker = useRef("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showScript, setShowScript] = useState(false);
  const [awaitingConcepts, setAwaitingConcepts] = useState(false);
  const status = job?.status;
  const concepts = job?.concepts || [];
  const hasConcepts = concepts.length > 0;
  const conceptsMarker = `${status}|${concepts.map((concept) => concept._id).join(",")}|${job?.updatedAt || ""}`;

  useEffect(() => {
    setShowScript(false);
  }, [active.creator.jobId]);

  useEffect(() => {
    if (status === CREATOR_SCRIPT_JOB_STATUS.ready) setShowScript(true);
    if (
      status === CREATOR_SCRIPT_JOB_STATUS.conceptualising ||
      status === CREATOR_SCRIPT_JOB_STATUS.waitingUserInputs ||
      status === CREATOR_SCRIPT_JOB_STATUS.waitingCreatorProfile
    ) {
      setShowScript(false);
    }
  }, [active.creator.jobId, status]);

  useEffect(() => {
    if (!awaitingConcepts) return;
    if (status === CREATOR_SCRIPT_JOB_STATUS.failed) {
      setAwaitingConcepts(false);
      return;
    }
    const stillLoading =
      status === CREATOR_SCRIPT_JOB_STATUS.conceptualising ||
      status === CREATOR_SCRIPT_JOB_STATUS.waitingCreatorProfile;
    if (!stillLoading && conceptsMarker !== submittedMarker.current) {
      setAwaitingConcepts(false);
    }
  }, [awaitingConcepts, conceptsMarker, status]);

  useEffect(() => {
    const queryIsPolling =
      status === CREATOR_SCRIPT_JOB_STATUS.conceptualising ||
      status === CREATOR_SCRIPT_JOB_STATUS.waitingCreatorProfile;
    if (!awaitingConcepts || queryIsPolling) return;
    void refetchJob();
    const id = window.setInterval(() => {
      void refetchJob();
    }, JOB_POLL_MS);
    return () => window.clearInterval(id);
  }, [awaitingConcepts, refetchJob, status]);

  const submitConcepts = () => {
    const draft = draftRef.current;
    if (!draft.brief.trim()) {
      ToastService.error("Add a brief before generating concepts.");
      return;
    }
    submittedMarker.current = conceptsMarker;
    setAwaitingConcepts(true);
    conceptualise({
      creatorScriptGeneratorJobId: draft.creator.jobId,
      userInputs: userInputs(draft),
    });
  };

  const askRegenerate = (revert?: () => void) => {
    if (confirmOpen) return;
    revertRef.current = revert || null;
    setConfirmOpen(true);
  };

  const acceptRegenerate = () => {
    setConfirmOpen(false);
    revertRef.current = null;
    submitConcepts();
  };

  const cancelRegenerate = () => {
    setConfirmOpen(false);
    revertRef.current?.();
    revertRef.current = null;
  };

  const regenerate = () => {
    if (!hasConcepts) {
      submitConcepts();
      return;
    }
    askRegenerate();
  };

  const changeSettings = (settings: ScriptSettings) => {
    if (!hasConcepts) {
      onChange({ settings });
      return;
    }
    if (confirmOpen) return;
    const previous = draftRef.current.settings;
    onChange({ settings });
    askRegenerate(() => onChangeRef.current({ settings: previous }));
  };

  const commitBrief = () => {
    if (!hasConcepts || confirmOpen) return;
    const submitted = job?.userInputs?.brief || "";
    if (draftRef.current.brief === submitted) return;
    askRegenerate(() => onChangeRef.current({ brief: submitted }));
  };

  const openScript = () => setShowScript(true);

  return {
    showScript,
    concepts,
    hasConcepts,
    busy:
      awaitingConcepts || status === CREATOR_SCRIPT_JOB_STATUS.conceptualising,
    confirmOpen,
    acceptRegenerate,
    cancelRegenerate,
    regenerate,
    changeSettings,
    commitBrief,
    openScript,
    backToConcepts: () => setShowScript(false),
    selectedFromJob: (id: string) =>
      concepts.find((concept) => concept._id === id) || null,
  };
}
